import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Home as HomeIcon, Building2, Sparkles, TruckIcon, Hotel, HardHat, ShoppingBag, Dumbbell, Warehouse, UtensilsCrossed, Star, CheckCircle, User } from 'lucide-react';
import { services, testimonials } from '../mock/data';
import GoogleReviews from '../components/GoogleReviews';

const iconMap = {
  Home: HomeIcon,
  Building2: Building2,
  Sparkles: Sparkles,
  TruckIcon: TruckIcon,
  Hotel: Hotel,
  HardHat: HardHat,
  ShoppingBag: ShoppingBag,
  Dumbbell: Dumbbell,
  Warehouse: Warehouse,
  UtensilsCrossed: UtensilsCrossed
};

const HomePage = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-black text-white py-20 md:py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black opacity-90"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
                Professional Cleaning 
                <span className="text-red-600"> Excellence</span> Since 1983
              </h1>
              <p className="text-xl text-gray-300 mb-8">
                Trusted residential and commercial cleaning services for Lexington, KY and surrounding cities.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/booking">
                  <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white w-full sm:w-auto">
                    Book a Consultation
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-black w-full sm:w-auto">
                    Get a Quote
                  </Button>
                </Link>
              </div>
            </div>
            <div className="hidden md:block">
              <img 
                src="https://images.pexels.com/photos/6195129/pexels-photo-6195129.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" 
                alt="Professional Cleaning Team" 
                className="rounded-lg shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Why Choose Taylormade?</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Over 40 years of excellence in professional cleaning services
            </p>
          </div>
          
          {/* Trust Badges */}
          <div className="flex flex-wrap justify-center gap-6 mb-12 pb-12 border-b border-gray-200">
            <div className="flex items-center gap-3 px-6 py-4 bg-red-50 rounded-lg">
              <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center">
                <CheckCircle className="w-7 h-7 text-white" />
              </div>
              <div>
                <p className="font-bold text-gray-900">Licensed & Insured</p>
                <p className="text-sm text-gray-600">Fully certified professionals</p>
              </div>
            </div>
            <div className="flex items-center gap-3 px-6 py-4 bg-red-50 rounded-lg">
              <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center">
                <Star className="w-7 h-7 text-white fill-white" />
              </div>
              <div>
                <p className="font-bold text-gray-900">40+ Years Experience</p>
                <p className="text-sm text-gray-600">Since 1983</p>
              </div>
            </div>
            <div className="flex items-center gap-3 px-6 py-4 bg-red-50 rounded-lg">
              <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center">
                <CheckCircle className="w-7 h-7 text-white" />
              </div>
              <div>
                <p className="font-bold text-gray-900">100% Satisfaction Guaranteed</p>
                <p className="text-sm text-gray-600">Quality you can trust</p>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { id: 'years', title: '40+ Years', description: 'Established in 1983', icon: Star },
              { id: 'team', title: 'Trusted Team', description: 'Background-checked professionals', icon: CheckCircle },
              { id: 'eco', title: 'Eco-Friendly', description: 'Safe, green cleaning products', icon: Sparkles },
              { id: 'schedule', title: 'Flexible Scheduling', description: 'Service on your timeline', icon: HomeIcon }
            ].map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.id} className="text-center group">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 text-red-600 rounded-full mb-4 group-hover:bg-red-600 group-hover:text-white transition-colors">
                    <Icon className="w-8 h-8" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Our Services</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Comprehensive cleaning solutions tailored to your needs
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => {
              const Icon = iconMap[service.icon];
              return (
                <Card key={service.id} className="hover:shadow-lg transition-shadow border-gray-200">
                  <CardHeader>
                    <div className="w-12 h-12 bg-red-100 text-red-600 rounded-lg flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6" />
                    </div>
                    <CardTitle className="text-xl">{service.title}</CardTitle>
                    <CardDescription>{service.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {service.features.slice(0, 3).map((feature) => (
                        <li key={`${service.id}-${feature.substring(0, 20)}`} className="flex items-center text-sm text-gray-600">
                          <CheckCircle className="w-4 h-4 text-red-600 mr-2 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          <div className="text-center mt-12">
            <Link to="/services">
              <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white">
                View All Services
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">What Our Clients Say</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Don't just take our word for it - hear from our satisfied customers
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="border-gray-200">
                <CardContent className="pt-6">
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={`${testimonial.id}-star-${i}`} className="w-5 h-5 text-red-600 fill-red-600" />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-4 italic">"{testimonial.content}"</p>
                  <div>
                    <p className="font-semibold">{testimonial.name}</p>
                    <p className="text-sm text-gray-600">{testimonial.role}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Google Reviews Section */}
      <GoogleReviews />

      {/* Referral Program CTA */}
      <section className="py-16 bg-gradient-to-r from-red-600 to-red-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full text-sm font-medium mb-4">
                <Star className="w-4 h-4 fill-white" />
                Refer & Save Program
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Love Our Service? Share It!
              </h2>
              <p className="text-xl text-white/90 mb-6">
                Refer a friend and you both get <strong>10% OFF</strong> your next cleaning. 
                No limits - refer as many friends as you'd like!
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/referral">
                  <Button size="lg" className="bg-white text-red-600 hover:bg-gray-100 w-full sm:w-auto">
                    Refer Now & Save
                  </Button>
                </Link>
                <Link to="/referral">
                  <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-red-600 w-full sm:w-auto">
                    Learn More
                  </Button>
                </Link>
              </div>
            </div>
            <div className="hidden md:block">
              <div className="bg-white/10 backdrop-blur-sm p-8 rounded-2xl border border-white/20">
                <div className="grid grid-cols-2 gap-6">
                  <div className="text-center">
                    <div className="text-4xl font-bold mb-2">10%</div>
                    <div className="text-white/80 text-sm">For You</div>
                  </div>
                  <div className="text-center">
                    <div className="text-4xl font-bold mb-2">10%</div>
                    <div className="text-white/80 text-sm">For Your Friend</div>
                  </div>
                  <div className="text-center col-span-2">
                    <div className="text-3xl font-bold mb-2">∞</div>
                    <div className="text-white/80 text-sm">Unlimited Referrals</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Links Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Quick <span className="text-red-600">Links</span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Easy access to all our services and information
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <Link to="/" className="group">
              <div className="bg-gray-50 hover:bg-red-50 rounded-lg p-6 text-center transition-all hover:shadow-md border border-gray-200 hover:border-red-600">
                <HomeIcon className="w-10 h-10 text-red-600 mx-auto mb-3 group-hover:scale-110 transition-transform" />
                <h3 className="font-semibold text-gray-900 group-hover:text-red-600">Home</h3>
              </div>
            </Link>
            
            <Link to="/services" className="group">
              <div className="bg-gray-50 hover:bg-red-50 rounded-lg p-6 text-center transition-all hover:shadow-md border border-gray-200 hover:border-red-600">
                <Sparkles className="w-10 h-10 text-red-600 mx-auto mb-3 group-hover:scale-110 transition-transform" />
                <h3 className="font-semibold text-gray-900 group-hover:text-red-600">Services</h3>
              </div>
            </Link>
            
            <Link to="/gallery" className="group">
              <div className="bg-gray-50 hover:bg-red-50 rounded-lg p-6 text-center transition-all hover:shadow-md border border-gray-200 hover:border-red-600">
                <Star className="w-10 h-10 text-red-600 mx-auto mb-3 group-hover:scale-110 transition-transform" />
                <h3 className="font-semibold text-gray-900 group-hover:text-red-600">Gallery</h3>
              </div>
            </Link>
            
            <Link to="/contact" className="group">
              <div className="bg-gray-50 hover:bg-red-50 rounded-lg p-6 text-center transition-all hover:shadow-md border border-gray-200 hover:border-red-600">
                <CheckCircle className="w-10 h-10 text-red-600 mx-auto mb-3 group-hover:scale-110 transition-transform" />
                <h3 className="font-semibold text-gray-900 group-hover:text-red-600">Contact</h3>
              </div>
            </Link>
            
            <Link to="/careers" className="group">
              <div className="bg-gray-50 hover:bg-red-50 rounded-lg p-6 text-center transition-all hover:shadow-md border border-gray-200 hover:border-red-600">
                <User className="w-10 h-10 text-red-600 mx-auto mb-3 group-hover:scale-110 transition-transform" />
                <h3 className="font-semibold text-gray-900 group-hover:text-red-600">Careers</h3>
              </div>
            </Link>
            
            <Link to="/faq" className="group">
              <div className="bg-gray-50 hover:bg-red-50 rounded-lg p-6 text-center transition-all hover:shadow-md border border-gray-200 hover:border-red-600">
                <CheckCircle className="w-10 h-10 text-red-600 mx-auto mb-3 group-hover:scale-110 transition-transform" />
                <h3 className="font-semibold text-gray-900 group-hover:text-red-600">FAQ</h3>
              </div>
            </Link>
            
            <Link to="/blog" className="group">
              <div className="bg-gray-50 hover:bg-red-50 rounded-lg p-6 text-center transition-all hover:shadow-md border border-gray-200 hover:border-red-600">
                <Building2 className="w-10 h-10 text-red-600 mx-auto mb-3 group-hover:scale-110 transition-transform" />
                <h3 className="font-semibold text-gray-900 group-hover:text-red-600">Blog</h3>
              </div>
            </Link>
            
            <Link to="/referral" className="group">
              <div className="bg-gray-50 hover:bg-red-50 rounded-lg p-6 text-center transition-all hover:shadow-md border border-gray-200 hover:border-red-600">
                <Star className="w-10 h-10 text-red-600 mx-auto mb-3 group-hover:scale-110 transition-transform" />
                <h3 className="font-semibold text-gray-900 group-hover:text-red-600">Refer a Friend</h3>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-black text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready for a <span className="text-red-600">Spotless</span> Space?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Get your free quote today and experience the Taylormade difference
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white w-full sm:w-auto">
                Request a Quote
              </Button>
            </Link>
            <Link to="/booking">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-black w-full sm:w-auto">
                Book Now
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
